// import { debounce } from '@mui/material';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import AG_GRID_LOCALE_IT from 'components/ag-grid/locale-it';
import CustomPagination from 'components/atomic/organisms/ProjectReporting/CustomPagination';
import DownloadExcelButton from 'components/form/DownloadExcelButton';
import StatusBar from 'components/StatusBar';
import { OETrans } from 'components/translation/OETrans';
import { translate } from 'components/translation/translation';
import { IcoMinusCircled, IcoUploadAction, Lens, TooltipInfoIcon } from 'icone';
import { exportLabeling, getLabeling, importLabeling, LabelingType } from 'lib/api_ts/labelingApi';
import { columnCentered } from 'lib/helpers/agGridHelpers';
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { ButtonToolbar, Col, Form, FormControl, InputGroup, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import { useLocation } from 'react-router-dom-v5-compat';

interface LabelingProps {}

const Labeling: React.FC<LabelingProps> = () => {
	const { pathname } = useLocation();
	const projectId = pathname.split('/')[2];

	const localeText = AG_GRID_LOCALE_IT;

	const gridRef = useRef<AgGridReact>(null);

	const [isPending, setIsPending] = useState(false);
	const [filterInputValue, setFilterInputValue] = useState('');

	const [isLoading, setIsLoading] = useState(false);
	const [rows, setRows] = useState<LabelingType[]>([]);
	const [count, setCount] = useState<number>();
	const [currentPage, setCurrentPage] = useState(0);
	const [perPage, setPerPage] = useState(100);
	const [defaultPerPage, setDefaultPerPage] = useState(100);

	// const [searchTerm, setSearchTerm] = useState('');
	// const debouncedSetSearchTermRef = useRef(
	// 	debounce((value) => {
	// 		setCurrentPage(0);
	// 		setPerPage(defaultPerPage);
	// 		if (gridRef.current) {
	// 			gridRef.current.api.paginationGoToPage(0);
	// 			gridRef.current.api.paginationSetPageSize(defaultPerPage);
	// 		}
	// 		setSearchTerm(value); // Solo l'aggiornamento finale dopo debounce
	// 	}, 500),
	// );
	// useEffect(() => {
	// 	debouncedSetSearchTermRef.current(filterInputValue);
	// }, [filterInputValue]);

	const onFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setFilterInputValue(value);
	};

	useEffect(() => {
		setCurrentPage(0);
		setPerPage(defaultPerPage);
		if (!gridRef.current || !gridRef.current.api) return;
		gridRef.current.api.setQuickFilter(filterInputValue);
		gridRef.current.api.paginationGoToPage(0);
		gridRef.current.api.paginationSetPageSize(defaultPerPage);
	}, [filterInputValue, defaultPerPage]);

	const blankStatusBar = {
		status: undefined,
		message: '',
		timeout: undefined,
	};

	const [statusBar, setStatusBar] = useState(blankStatusBar);
	const resetStatusBar = () => {
		setStatusBar(blankStatusBar);
	};

	const columnDefs = [
		{
			headerName: 'Codice impianto',
			field: 'plant.plantCode',
		},
		{
			headerName: 'Denominazione impianto',
			field: 'plant.plantName',
		},
		{
			headerName: 'Localizzazione (Regione)',
			field: 'plant.area',
		},
		{
			headerName: 'Unità territoriale',
			field: 'plant.ut',
		},
		{
			headerName: 'Centro di costo',
			field: 'ot.code',
		},
		{
			headerName: 'Matricola bene',
			field: 'serialNumber',
		},
		{
			headerName: 'Descrizione bene',
			field: 'serialNumberDescription',
		},
		{
			headerName: 'Codice materiale',
			field: 'material.code',
		},
		{
			headerName: 'Check',
			field: 'color',
			// custom cell renderer per semaforo
			cellRenderer: (params: { value: 'green' | 'yellow' | 'white_na' }) => (
				<Row className="align-items-center justify-content-center h-100">
					<Col className="col-auto">
						{(() => {
							const colorsMap = {
								yellow: '#D4BE31',
								green: '#689689',
								white_na: '#D4D4D4',
							} as const;
							const defaultStyles = {
								display: 'block',
								borderRadius: '100%',
								width: '1.2em',
								height: '1.2em',
							} as const;

							switch (params.value) {
								case 'green':
								case 'yellow':
									return (
										<i
											style={{
												...defaultStyles,
												backgroundColor: colorsMap[params.value],
												border: `2px solid ${colorsMap[params.value]}`,
											}}
										/>
									);
								case 'white_na':
									return (
										<IcoMinusCircled
											style={{
												...defaultStyles,
												color: colorsMap[params.value],
												width: `calc(${defaultStyles.width} + 2px)`,
												height: `calc(${defaultStyles.height} + 2px)`,
											}}
										/>
									);
							}
						})()}
						<span />
					</Col>
				</Row>
			),
			...columnCentered(),
			maxWidth: 130,
		},
	];

	const defaultColDef = {
		resizable: true,
		flex: 1,
		suppressMovable: true,
		sortable: true,
		filterParams: {
			suppressAndOrCondition: true, //for disable AND/OR login in filter
			filterOptions: ['contains'],
		},
		cellStyle: {
			padding: '3px 1rem',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			overflow: 'hidden',
		},
	};

	const fetchData = useCallback(async () => {
		setIsLoading(true);
		try {
			const { data, meta } = await getLabeling(projectId, {
				currentPage: currentPage + 1,
				perPage,
				// searchTerm,
			});
			setRows(data);
			setCount(meta.total);
			if (meta.perPage !== perPage) {
				setPerPage(meta.perPage);
				setDefaultPerPage(meta.perPage);
			}
		} catch (error) {
			console.error('Error in Labeling component:', error);
		} finally {
			setIsLoading(false);
		}
	}, [projectId, currentPage, perPage /*, searchTerm */]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const hanldeUpload = async (event: ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			setIsPending(true);
			setStatusBar({
				status: 'elaborating',
				message: 'Caricamento file in corso',
				timeout: false,
			});

			try {
				const response = await importLabeling(projectId, file);
				if (!response.success) {
					throw new Error('Failed to upload file');
				}
				setStatusBar({
					status: 'success',
					message: 'File caricato correttamente',
					timeout: true,
				});
				setTimeout(() => {
					resetStatusBar();
					fetchData();
				}, 2000);
			} catch (error) {
				console.error('Error uploading file:', error);
				setStatusBar({
					status: 'error',
					message: 'Errore durante il caricamento del file: ' + (error as Error).message,
					timeout: true,
				});
				setTimeout(() => {
					resetStatusBar();
				}, 2000);
			} finally {
				setIsPending(false);
				event.target.value = '';
			}
		}
	};

	return (
		<>
			{statusBar.status ? (
				<StatusBar status={statusBar.status} message={statusBar.message} timeout={statusBar.timeout}>
					<></>
				</StatusBar>
			) : null}
			<Row className="align-items-center mt-4 mb-3">
				<Col className="col-auto">
					<Form.Group controlId={`labeling${projectId}_1`} className="position-relative">
						<Form.Label
							style={{
								cursor: !isPending ? 'pointer' : 'default',
								marginBottom: 0,
								pointerEvents: !isPending ? 'all' : 'none',
							}}
							className="d-flex align-items-center"
						>
							<div
								className={`btn btn-outlined d-flex align-items-center justify-content-between ${isPending ? 'disabled' : ''}`.trim()}
								style={
									isPending
										? {
												pointerEvents: 'none',
												cursor: 'default',
												color: '#abb7c3',
												borderColor: '#abb7c3',
											}
										: {}
								}
							>
								<IcoUploadAction className={`ico-x-small download-upload me-1 align-bottom ${isPending ? 'disabled' : 'second-color'}`} />
								<span>
									<OETrans type="propTrans" code="common.importdata" fallback={'Importa dati'} />
								</span>
							</div>
							<OverlayTrigger
								delay={{ show: 200, hide: 400 }}
								overlay={(props) => (
									<Tooltip id="button-tooltip" {...props}>
										<>
											<OETrans type="propTrans" code="common.filesallowed" fallback={'Ammessi solo file'} />:
											<ul style={{ textAlign: 'left', paddingLeft: '20px', margin: 'auto' }}>
												<li>
													<OETrans type="propTrans" code="common.extensionxls" fallback={'con estensione .xls, .xlsx'} />
												</li>
											</ul>
										</>
									</Tooltip>
								)}
							>
								<TooltipInfoIcon className="tooltip-ico position-absolute start-100" style={{ width: '1rem' }} />
							</OverlayTrigger>
						</Form.Label>
						<Form.Control
							type="file"
							accept=".xlsx"
							// disabled={isPending ? true : false}
							style={{ display: 'none' }}
							onChange={hanldeUpload}
						/>
					</Form.Group>
				</Col>
				<Col className="col-auto ms-auto py-1">
					<ButtonToolbar className="justify-content-end">
						<DownloadExcelButton
							apiDownload={exportLabeling}
							apiProps={[projectId]}
							// apiDownload={downloadProjectDocument}
							// apiProps={[project.id, 'plantsandmaterials', modelFilterString]}
						/>
						<InputGroup className="search-table align-items-center mt-md-0 ms-1">
							<FormControl
								type="text"
								className="filter-text-box"
								placeholder={translate('common.search', 'Cerca') + '...'}
								value={filterInputValue}
								onChange={onFilterChange}
							/>
							<div style={{ width: '40px', textAlign: 'center' }}>
								<Lens className="lens" />
							</div>
						</InputGroup>
					</ButtonToolbar>
				</Col>
			</Row>
			<Row>
				{Array.isArray(rows) ? (
					<Col className="col-12 col-xxl-9" id="grid-wrapper">
						<div className="ag-theme-alpine list-table">
							<AgGridReact
								ref={gridRef}
								columnDefs={columnDefs}
								rowData={rows}
								// getRowStyle={getRowStyle}
								defaultColDef={defaultColDef}
								domLayout="autoHeight"
								tooltipShowDelay={0}
								tooltipHideDelay={2000}
								tooltipMouseTrack={true}
								// onGridReady={(e) => handleData(e, gridRef)}
								// onFirstDataRendered={(e) => handleData(e, gridRef)}
								//onModelUpdated={(e) => handleData(e, gridRef)}
								// onFilterChanged={onFilterChanged}
								headerHeight={50}
								rowHeight={50}
								// ariaDescription="ag-grid-table"
								suppressCellFocus={true}
								rowSelection={'multiple'}
								// onSelectionChanged={onSelectionChanged}
								suppressRowClickSelection={true}
								rowMultiSelectWithClick={true}
								localeText={localeText}
								overlayLoadingTemplate="<span class='ag-overlay-loading-center'>Loading...</span>" // Template dell'overlay di caricamento
								// {...(handleItemListSelectionProps && { components: { customHeader: handleItemListSelectionProps[5] } })}
							/>
							<CustomPagination
								count={count || 100}
								currentPage={currentPage}
								gridRef={gridRef}
								loading={isLoading}
								perPage={perPage || 100}
								setCurrentPage={setCurrentPage}
								setPerPage={setPerPage}
							/>
						</div>
					</Col>
				) : (
					<div className="text-center my-4">
						<Spinner animation="border" role="status">
							<span style={{ visibility: 'hidden' }}>
								<OETrans type="propTrans" code="common.loading" fallback={'Caricamento'} />
								...
							</span>
						</Spinner>
					</div>
				)}
			</Row>
		</>
	);
};

export default Labeling;
